import { ArrowLeft, Inbox, Loader2, AlertTriangle, X } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { fileUrl, isVideo } from "../lib/api";

const cx = (...a) => a.filter(Boolean).join(" ");
export { cx };

export function Page({ title, subtitle, back, right, children, testid, wide, titleClass }) {
  const nav = useNavigate();
  return (
    <div className="min-h-full" data-testid={testid}>
      <header className="sticky top-0 z-20 border-b border-wf-line bg-wf-surface/90 backdrop-blur-xl">
        <div className={cx("mx-auto flex flex-wrap items-center gap-3 px-5 py-4 lg:px-10", wide ? "max-w-7xl" : "max-w-5xl")}>
          {back && (
            <button data-testid="page-back-button" onClick={() => nav(-1)} className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-wf-brand transition-colors hover:bg-wf-soft">
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="min-w-[180px] flex-1">
            <h1 data-testid="page-title" className={cx("truncate text-2xl font-extrabold tracking-tight lg:text-3xl", titleClass)}>{title}</h1>
            {subtitle && <p className="truncate text-sm text-wf-mute">{subtitle}</p>}
          </div>
          {right && <div className="flex max-w-full flex-wrap items-center justify-end gap-2">{right}</div>}
        </div>
      </header>
      <main className={cx("mx-auto px-5 pb-16 pt-6 lg:px-10", wide ? "max-w-7xl" : "max-w-5xl")}>{children}</main>
    </div>
  );
}

const BTN = {
  primary: "bg-wf-brand text-wf-onbrand hover:opacity-90",
  secondary: "bg-wf-soft text-wf-softink hover:brightness-95",
  ghost: "text-wf-brand hover:bg-wf-soft",
  danger: "bg-wf-err text-white hover:opacity-90",
  outline: "border border-wf-line bg-wf-surface text-wf-ink hover:bg-wf-s2",
};
export function Btn({ variant = "primary", icon: Icon, loading, children, className, size = "md", ...p }) {
  return (
    <button
      {...p}
      disabled={p.disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-[opacity,background-color,transform] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "h-9 px-3 text-sm" : "h-11 px-5 text-[15px]",
        BTN[variant],
        className,
      )}
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : Icon && <Icon size={18} />}
      {children}
    </button>
  );
}

export function IconBtn({ icon: Icon, tone = "brand", className, ...p }) {
  const c = tone === "danger" ? "text-wf-err hover:bg-red-50" : "text-wf-brand hover:bg-wf-soft";
  return (
    <button {...p} className={cx("flex h-10 w-10 items-center justify-center rounded-full transition-colors disabled:opacity-50", c, className)}>
      {p.disabled ? <Loader2 size={18} className="animate-spin" /> : <Icon size={20} />}
    </button>
  );
}

const inputCls = "w-full rounded-xl border border-wf-line bg-wf-s2 px-4 text-[15px] text-wf-ink outline-none transition-colors placeholder:text-wf-mute focus:border-wf-brand focus:bg-wf-surface";
export function Field({ label, value, onChange, multiline, testid, className, hint, ...p }) {
  return (
    <label className={cx("mb-4 block", className)}>
      {label && <span className="mb-1.5 block text-[13px] font-semibold text-wf-ink2">{label}</span>}
      {multiline ? (
        <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} className={cx(inputCls, "py-3")} {...p} />
      ) : (
        <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cx(inputCls, "h-12")} {...p} />
      )}
      {hint && <span className="mt-1 block text-xs text-wf-mute">{hint}</span>}
    </label>
  );
}

/** Text field with tappable suggestions from earlier entries. */
export function SuggestField({ suggestions = [], value, onChange, ...p }) {
  const q = (value ?? "").toLowerCase();
  const list = [...new Set(suggestions)].filter((s) => s && s.toLowerCase() !== q && (!q || s.toLowerCase().includes(q))).slice(0, 6);
  return (
    <div className="mb-4">
      <Field {...p} value={value} onChange={onChange} className="mb-2" />
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${p.testid}-suggestions`}>
          {list.map((s) => (
            <button key={s} type="button" onClick={() => onChange(s)} className="rounded-full bg-wf-s3 px-3 py-1 text-xs font-medium text-wf-ink2 hover:bg-wf-soft">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Select({ label, value, options, onChange, testid, placeholder, className, disabled }) {
  return (
    <label className={cx("mb-4 block", className)}>
      {label && <span className="mb-1.5 block text-[13px] font-semibold text-wf-ink2">{label}</span>}
      <select
        data-testid={testid}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cx(inputCls, "h-12 cursor-pointer appearance-none bg-[length:16px] bg-[right_14px_center] bg-no-repeat pr-10")}
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%238E8E93' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")" }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          return <option key={v} value={v}>{typeof o === "string" ? o : o.label}</option>;
        })}
      </select>
    </label>
  );
}

export function Chip({ label, selected, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cx(
        "min-h-[38px] rounded-full border px-4 text-sm font-semibold transition-colors",
        selected ? "border-wf-brand bg-wf-brand text-wf-onbrand" : "border-wf-line bg-wf-s2 text-wf-ink2 hover:border-wf-brand2",
      )}
    >
      {label}
    </button>
  );
}

export function StatusPill({ label, testid }) {
  if (!label) return null;
  const paid = label.trim().toLowerCase() === "paid";
  return (
    <span data-testid={testid} className={cx("inline-flex max-w-full truncate rounded-full px-2.5 py-1 text-xs font-bold", paid ? "bg-green-100 text-green-800" : "bg-wf-soft text-wf-softink")}>
      {label}
    </span>
  );
}

const TONE = { new: "bg-wf-brand text-wf-onbrand", ok: "bg-wf-ok text-white", warn: "bg-wf-warn text-white", over: "bg-wf-err text-white" };
export function Badge({ label, tone, testid }) {
  return (
    <span data-testid={testid} className={cx("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-extrabold tracking-wide", TONE[tone])}>
      {(tone === "warn" || tone === "over") && <AlertTriangle size={11} />}
      {label}
    </span>
  );
}

export function Empty({ icon: Icon = Inbox, title, subtitle, testid }) {
  return (
    <div data-testid={testid ?? "empty-state"} className="flex flex-col items-center px-6 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-wf-soft text-wf-brand">
        <Icon size={28} />
      </div>
      <p className="text-lg font-bold">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-sm text-wf-mute">{subtitle}</p>}
    </div>
  );
}

export const Loading = () => (
  <div data-testid="loading" className="flex justify-center py-24 text-wf-brand">
    <Loader2 className="animate-spin" size={28} />
  </div>
);

export function Section({ title, actionLabel, onAction, testid, children }) {
  return (
    <div className="mb-2 mt-8 flex items-center justify-between">
      <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute">{title}</h2>
      {children}
      {actionLabel && (
        <button data-testid={testid} onClick={onAction} className="rounded-lg px-2 py-1 text-sm font-bold text-wf-brand hover:bg-wf-soft">
          + {actionLabel}
        </button>
      )}
    </div>
  );
}

export const Card = ({ children, className, testid, ...p }) => (
  <div data-testid={testid} className={cx("rounded-2xl border border-wf-line bg-wf-s2 p-5", className)} {...p}>{children}</div>
);

export function InfoRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="border-b border-wf-line py-2.5 last:border-0">
      <p className="text-xs font-semibold text-wf-mute">{label}</p>
      <p data-testid={testid} className="whitespace-pre-wrap text-[15px] text-wf-ink">{value}</p>
    </div>
  );
}

export function Avatar({ name, src, size = 40 }) {
  const initials = (name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={fileUrl(src)} alt={name} style={{ width: size, height: size }} className="shrink-0 rounded-full object-cover" />
  ) : (
    <div style={{ width: size, height: size }} className="flex shrink-0 items-center justify-center rounded-full bg-wf-soft text-sm font-bold text-wf-softink">
      {initials}
    </div>
  );
}

export function Modal({ open, onClose, title, children, testid, wide, lockOutside }) {
  const block = (e) => lockOutside && e.preventDefault();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid} onPointerDownOutside={block} onInteractOutside={block} onEscapeKeyDown={block} className={cx("max-h-[90vh] overflow-y-auto rounded-2xl bg-wf-surface", wide ? "max-w-3xl" : "max-w-md")}>
        {title && <DialogTitle className="text-lg font-extrabold">{title}</DialogTitle>}
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function Confirm({ open, title, message = "This cannot be undone.", onConfirm, onClose, confirmLabel = "Delete" }) {
  return (
    <Modal open={open} onClose={onClose} title={title} testid="confirm-dialog">
      <p className="text-sm text-wf-mute">{message}</p>
      <div className="mt-4 flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose} data-testid="confirm-cancel">Cancel</Btn>
        <Btn variant="danger" data-testid="confirm-ok" onClick={() => { onClose(); onConfirm(); }}>{confirmLabel}</Btn>
      </div>
    </Modal>
  );
}

/** Full-screen photo / video viewer with previous/next when a list is given. */
export function MediaViewer({ path, list, onChange, onClose, onRemove }) {
  React.useEffect(() => {
    if (!path) return undefined;
    const i = list?.indexOf(path) ?? -1;
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (i < 0 || !onChange) return;
      if (e.key === "ArrowRight" && i < list.length - 1) onChange(list[i + 1]);
      if (e.key === "ArrowLeft" && i > 0) onChange(list[i - 1]);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [path, list, onChange, onClose]);
  if (!path) return null;
  const i = list?.indexOf(path) ?? -1;
  const nav = (d) => onChange?.(list[i + d]);
  return (
    <div data-testid="media-viewer" className="fixed inset-0 z-[100] flex flex-col bg-black/95 pointer-events-auto">
      <div className="flex items-center justify-end gap-2 p-4">
        {i >= 0 && list.length > 1 && <span data-testid="media-viewer-pager" className="mr-auto text-sm font-semibold text-white/80">{i + 1} / {list.length}</span>}
        {onRemove && <Btn variant="danger" size="sm" data-testid="media-viewer-remove" onClick={onRemove}>Remove</Btn>}
        <button data-testid="media-viewer-close" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
          <X size={20} />
        </button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-4">
        {i > 0 && onChange && <button data-testid="media-viewer-prev" onClick={() => nav(-1)} className="absolute left-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl text-white hover:bg-white/25">‹</button>}
        {i >= 0 && i < list.length - 1 && onChange && <button data-testid="media-viewer-next" onClick={() => nav(1)} className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl text-white hover:bg-white/25">›</button>}
        {isVideo(path) ? (
          <video src={fileUrl(path)} controls autoPlay className="max-h-full max-w-full" />
        ) : (
          <img src={fileUrl(path)} alt="" className="max-h-full max-w-full object-contain" />
        )}
      </div>
    </div>
  );
}

export function FilePickButton({ accept = "image/*", multiple, onFiles, children, testid, className, variant = "secondary", icon, loading }) {
  const ref = React.useRef(null);
  return (
    <>
      <input ref={ref} type="file" hidden accept={accept} multiple={multiple} data-testid={`${testid}-input`}
        onChange={(e) => { const f = [...e.target.files]; e.target.value = ""; if (f.length) onFiles(f); }} />
      <Btn type="button" variant={variant} icon={icon} loading={loading} className={className} data-testid={testid} onClick={() => ref.current.click()}>
        {children}
      </Btn>
    </>
  );
}

export function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cx(inputCls, "h-11 bg-wf-s3")}
    />
  );
}
